import { type Injector, runInInjectionContext } from '@angular/core';
import { ActivityEntityName, JobItemCommandId, type PurchaseOrder } from '@jms/model';
import { injectQueryClient } from '@ngneat/query';
import { EntityCreatorService } from '@workbuddy/modules/creators';
import { EntityDataServiceRaw, type EntityManifestBulkActionHandler } from '@workbuddy/services/entities';
import { ToastService } from '@workbuddy/ui/services';
import type { BulkCommandArgs } from '@workbuddy/ui/v2/blocks/entity.board/context';
import { lastValueFrom } from 'rxjs';
import { resolveItemRecord } from '../row/item.row.record';
import { isItemPurchasable } from '../support/item.purchasable';

export const ITEMS_CREATE_PURCHASE_ORDER_BULK_ACTION = 'items.purchase.order.create';

/**
 * Bulk "Create Purchase Order" for JobItem. One order comes from the whole selection, so this is not a
 * per-row pipeline action: the server builds the draft from the eligible lines and the legacy Purchase
 * Order creator opens on it — saving there is what links the lines, as the legacy Items tab did.
 */
export const handleJobItemPurchaseOrderBulkAction: EntityManifestBulkActionHandler = async (args: BulkCommandArgs, injector: Injector) => {

  const actionId = args.bulkActionRequest?.actionId ?? args.action;
  if (actionId !== ITEMS_CREATE_PURCHASE_ORDER_BULK_ACTION) return { status: 'not-handled' };

  const toast = injector.get(ToastService);
  const rows = args.data ?? [];
  const jobId = resolveSharedJobId(rows);
  const itemIds = rows.map(row => resolveItemRecord(row)).filter(isItemPurchasable).map(item => item._id as string);

  if (!jobId || !itemIds.length) {
    toast.info('No items to order', 'Select authorised items that are not yet on a purchase order or bill.');
    return { status: 'cancelled' };
  }

  let purchaseOrder: PurchaseOrder | null;
  try {
    purchaseOrder = await lastValueFrom(injector.get(EntityDataServiceRaw).executeCommand<PurchaseOrder>(ActivityEntityName.JobItem, JobItemCommandId.CreatePurchaseOrder, { jobId, itemIds }));
  } catch (error) {
    toast.error('Purchase Order was not created', errorMessage(error));
    return { status: 'cancelled' };
  }
  if (!purchaseOrder) return { status: 'cancelled' };

  // The legacy creator closes without a result on save and on cancel alike, so the board re-reads either
  // way: a saved order stamps itself onto the lines, which the status cell shows as the "O" badge.
  await injector.get(EntityCreatorService).show<ActivityEntityName.PurchaseOrder, void>({
    entity: ActivityEntityName.PurchaseOrder,
    config: { data: { purchaseOrder } },
  });
  // @ngneat/query provides its client through an injection token, not the QueryClient class.
  const queryClient = runInInjectionContext(injector, injectQueryClient);
  void queryClient.invalidateQueries({ queryKey: [ActivityEntityName.JobItem, 'items'], refetchType: 'all' });

  return { status: 'handled' };
};

/** Rows are parent-shaped jobs; a job-level board holds one job, and an order is one job's. */
function resolveSharedJobId(rows: unknown[]): string | null {

  const ids = rows.map(row => (row as { _id?: string } | null)?._id);
  const first = ids[0];
  if (!first || ids.some(id => id !== first)) return null;

  return first;
}

function errorMessage(error: unknown): string {

  const body = (error as { error?: { message?: unknown } } | null)?.error;
  if (typeof body?.message === 'string') return body.message;
  if (error instanceof Error) return error.message;

  return 'An unknown error has occurred';
}
